import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client.js';
import type { Organization } from '@/pages/tenancy/types.js';

/**
 * The platform's own denomination, for the ONE thing that legitimately has no
 * brand behind it: the platform-scope finance-config row's stored currency
 * code, which an operator picks and svc-finance stores beside the figures.
 *
 * It is NOT an answer to "what is this agent's currency". Substituting it there
 * is the BO-05 failure — a JPY brand's stored minor units read and re-emitted at
 * a 2-decimal exponent, 100× wrong in both directions with the digits on screen
 * never moving. `lib/money/money-input-currency.test.ts` refuses it in a money
 * box's `currency` prop for that reason, by name and through an alias.
 */
export const PLATFORM_DEFAULT_CURRENCY = 'THB';

/**
 * The fields of an organization row a money screen needs: which brand, and what
 * currency its figures are in. `GET /organizations/brief` answers exactly these.
 */
export type OrganizationBrief = Pick<
  Organization,
  'id' | 'kind' | 'parentId' | 'name' | 'displayName' | 'status' | 'currency' | 'tenantId'
>;

/**
 * The brand list every lookup in this file rides on, and `useOwnableAgents`
 * with it. One query, one cache entry. `undefined` means the request is in
 * flight OR failed — the two states no caller may tell apart, and both of which
 * mean "nothing has been read".
 *
 * The brief read, not `GET /organizations`: the full list is gated on
 * `tenancy:read`, the code that opens the Organization menu, and a cashier
 * trimmed of that menu still has to know which currency an amount is in.
 */
export function useOrganizationBriefQuery() {
  return useQuery<{ items: OrganizationBrief[] }>({
    queryKey: ['tenancy', 'organizations', 'brief'],
    queryFn: () => api.get<{ items: OrganizationBrief[] }>('/api/v1/organizations/brief'),
    staleTime: 5 * 60_000,
  });
}

function useOrganizationBrief(): OrganizationBrief[] | undefined {
  return useOrganizationBriefQuery().data?.items;
}

/**
 * The currency ONE agent's money is denominated in, in the same three states
 * {@link useAgentCurrencyMap} answers in — see the contract on
 * {@link AgentCurrencyResolver} below.
 *
 * It used to answer the platform default for a null id AND while the org list
 * was in flight or after it failed, which collapsed the two states a money box
 * has to tell apart. "There is no brand here" is a real answer a box can render
 * — raw minor units, which round-trip whatever the true exponent turns out to
 * be. "Nobody has read the brand yet" is not an answer at all, and rendering it
 * as THB is the BO-05 100× shape behind a race: the operator types under the
 * guess, and `MoneyInput`'s late-currency effect re-prices their digits the
 * moment the real code lands. `useAgentCurrencyMap` stopped doing that for the
 * finance TABLES (N6); this is the same fix for the FORMS.
 *
 * Callers withhold the box on `undefined` — no box, an explanation in its place
 * — and may offer the minor-units treatment on `null`.
 */
export function useAgentCurrency(agentId: string | null | undefined): string | null | undefined {
  return makeAgentCurrencyResolver(useOrganizationBrief())(agentId);
}

/**
 * Three answers, and the difference between the last two is the whole point.
 *
 * - a code — that agent settles in it.
 * - `null` — READ, and there is no currency to report: no agent id was asked
 *   about, or the row was found and records none. `organizations.currency` is
 *   nullable, so this is a real row a real coupon can belong to.
 * - `undefined` — NOT READ: the org query is in flight or failed, or the id is
 *   outside the list that came back.
 *
 * Both non-code answers are falsy, so a caller that only asks "do I have a
 * currency" keeps working unchanged. A caller that can offer a currency-less
 * treatment — raw minor units, which round-trip whatever the true exponent
 * turns out to be — must take `null` for that and withhold on `undefined`,
 * because "not read yet" is the state where a substituted exponent silently
 * rewrites a stored figure by 100×.
 *
 * On an authorisation surface neither is the platform default and neither may
 * be rendered as one.
 */
export type AgentCurrencyResolver = (
  agentId: string | null | undefined,
) => string | null | undefined;

/**
 * Pure form of the list lookup, so the dialogs and their tests share one rule
 * rather than each carrying a copy.
 */
export function makeAgentCurrencyResolver(items: OrganizationBrief[] | undefined): AgentCurrencyResolver {
  if (!items) return () => undefined;
  const byId = new Map(items.map((o) => [o.id, o.currency]));
  return (agentId) => {
    if (!agentId) return null;
    if (!byId.has(agentId)) return undefined;
    return byId.get(agentId) || null;
  };
}

/**
 * The same lookup, for a LIST of rows — and it answers `null`/`undefined` rather
 * than a default when it does not know.
 *
 * A hook cannot be called per row, and the finance tabs need one currency per
 * row: an operator's scope can span agents denominated differently, and a
 * withdrawal approved under the wrong glyph is the exact mis-click BO-05 exists
 * to stop — a JPY payout of ¥250,000 presented as "฿2,500.00" is a 100×
 * understatement on the authorisation screen itself (H8).
 *
 * Substituting the platform default recreated H8 behind a race instead of a
 * constant: `data` is `undefined` while the query is in flight AND after any
 * failure, so opening Withdraws and clicking Approve before
 * `/api/v1/organizations` resolves — a plain network round-trip, since the
 * finance tabs render no `AgentSelect` to have warmed the cache — presented
 * every row as THB. `OrganizationDto.currency` is nullable too, which fell
 * through `||` to the same place (N6).
 *
 * On an authorisation surface, absence must be shown as absence. Callers render
 * the unknown state and refuse the action until it resolves. `useAgentCurrency`
 * above answers in exactly the same three states for a single agent, and its
 * callers — money INPUTS — withhold the box rather than price it under a guess.
 */
export function useAgentCurrencyMap(): AgentCurrencyResolver {
  return makeAgentCurrencyResolver(useOrganizationBrief());
}

/** `null` = no name to show: nothing read, or no row with that id. */
export type AgentNameResolver = (agentId: string | null | undefined) => string | null;

/**
 * A brand's display name, from the UNFILTERED org list.
 *
 * `useOwnableAgents` keeps only active `kind === 'agent'` rows, because those
 * are the ones a new row may be handed to. Naming a brand is a different
 * question: a coupon, a withdrawal or a wallet already belongs to whatever it
 * belongs to, including a suspended brand or a master node, and a sentence about
 * it that renders a raw UUID tells the operator nothing.
 */
export function makeAgentNameResolver(items: OrganizationBrief[] | undefined): AgentNameResolver {
  if (!items) return () => null;
  const byId = new Map(items.map((o) => [o.id, o.name]));
  return (agentId) => (agentId ? byId.get(agentId) || null : null);
}

export function useAgentNameMap(): AgentNameResolver {
  return makeAgentNameResolver(useOrganizationBrief());
}
